// src/store/slices/authSlice.ts
// Authentication state management

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthUser } from '../../api/authApi';

const TOKEN_STORAGE_KEY = 'levelup_auth_token';
const USER_STORAGE_KEY = 'levelup_auth_user';

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isInitialized: boolean;
}

// Load initial state from localStorage
function loadInitialState(): AuthState {
  try {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const userJson = localStorage.getItem(USER_STORAGE_KEY);
    const user = userJson ? JSON.parse(userJson) : null;

    return {
      token,
      user,
      isInitialized: true,
    };
  } catch {
    return {
      token: null,
      user: null,
      isInitialized: true,
    };
  }
}

const initialState: AuthState = loadInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ token: string; user: AuthUser }>
    ) => {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
      state.isInitialized = true;

      // Persist to localStorage
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    },

    updateUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(action.payload));
    },

    logout: (state) => {
      state.token = null;
      state.user = null;

      // Clear localStorage
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(USER_STORAGE_KEY);
    },

    setInitialized: (state) => {
      state.isInitialized = true;
    },
  },
});

export const { setCredentials, updateUser, logout, setInitialized } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user;
export const selectCurrentToken = (state: { auth: AuthState }) => state.auth.token;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  Boolean(state.auth.token);
export const selectIsInitialized = (state: { auth: AuthState }) =>
  state.auth.isInitialized;
